import type {
  SimpleChartData,
  SimpleChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import {
  CurrencyValue,
  formatNumber,
  BarChart,
  LineChart,
  formatDatetime
} from '@blockanalitica/ui';
import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { ReactNode } from 'react';
import { useChartPeriod } from '@/hooks';

export default function HistoricGraphs({
  data,
  type,
  timeFilter,
  chartDaysAgo
}: {
  data: SimpleChartData[];
  type: string;
  timeFilter: ReactNode;
  chartDaysAgo: string;
}) {
  const theme = useTheme();
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();

  const [datasets, chartType] = useMemo(() => {
    let datasets: SimpleChartDataset[];
    let chartType: string;
    if (type === 'activity') {
      chartType = 'bar';
      datasets = [
        {
          xField: 'date',
          yField: 'borrow_volume',
          label: 'Borrow',
          backgroundColor: theme.colors.chartUp
        },
        {
          xField: 'date',
          yField: 'repay_volume',
          label: 'Repay',
          backgroundColor: theme.colors.chartDown
        }
      ];
    } else {
      chartType = 'line';
      datasets = [
        {
          xField: 'date',
          yField: type
        }
      ];
    }

    return [datasets, chartType];
  }, [type, theme.colors]);

  const valueFormatter = useCallback(
    (value: number) => {
      if (chartType === 'bar') {
        return <CurrencyValue value={value} options={{ currency: 'USD' }} />;
      } else {
        switch (type) {
          default:
            return (
              <CurrencyValue
                value={value}
                options={{
                  currency: 'DAI',
                  iconSize: '3'
                }}
              />
            );
        }
      }
    },
    [type, chartType]
  );

  const yScaleValueFormatter = useCallback((value: number) => {
    return formatNumber(value);
  }, []);

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  return (
    <>
      {chartType === 'bar' ? (
        <BarChart
          key={type}
          data={data}
          datasets={datasets}
          valueFormatter={valueFormatter}
          timeFilter={timeFilter}
          config={config}
          subValueFormatter={subValueFormatter}
        />
      ) : (
        <LineChart
          key={type}
          data={data}
          datasets={datasets}
          valueFormatter={valueFormatter}
          yScaleValueFormatter={yScaleValueFormatter}
          timeFilter={timeFilter}
          config={config}
        />
      )}
    </>
  );
}
